import { useCallback, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import type { PipelineId } from '@ludus/core'
import { listFlowRuns, type FlowRun, type FlowRunsPage } from '@/lib/api/flows.ts'

/**
 * A flow's runs, newest first, with keyset paging and polling while one is live.
 *
 * **Pages are held here, not in SWR.** SWR caches one value per key, and the
 * "Load older" gesture is an *append* rather than a replacement — so the pages
 * already fetched live in local state and only the first page is a cached read.
 * That keeps the poll cheap: a running flow re-fetches page one every few
 * seconds, and the older pages a person deliberately loaded are not re-fetched
 * with it.
 *
 * **Polling is conditional on the data, not on a flag.** `refreshInterval` is a
 * function of whether any run on the first page is still `running`, which is the
 * `useSyncRun` shape: a settled list stops polling by itself, and nothing has to
 * remember to turn it off. A run that starts while the panel is open is picked
 * up by the next poll of page one, because a new run is always newest.
 */

/** How often a live run is re-read. Slow enough to be polite, fast enough to watch. */
const POLL_MS = 3000
const PAGE_SIZE = 20

export interface FlowRunsFeed {
  runs: FlowRun[]
  isLoading: boolean
  error: unknown
  /** `null` when the engine says there is nothing older. */
  nextCursor: string | null
  loadingOlder: boolean
  loadOlder: () => void
  /** True while at least one loaded run is still going. */
  live: boolean
}

export function useFlowRuns(pipelineId: PipelineId, flowId: string): FlowRunsFeed {
  const [older, setOlder] = useState<FlowRun[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loadingOlder, setLoadingOlder] = useState(false)

  // Dropped when the flow changes. One `<FlowPage>` element serves every flow
  // route, so navigating from one flow to another re-renders this hook rather
  // than remounting it: without the reset, the pages loaded for the previous
  // flow stayed in `older` and were concatenated onto the new flow's list — and
  // `cursor` was still the previous flow's keyset token, so "Load older" paged
  // through one flow's history under another's heading.
  useEffect(() => {
    setOlder([])
    setCursor(null)
  }, [flowId, pipelineId])

  const key = `/pipelines/${pipelineId}/flows/${flowId}/runs`
  const first = useSWR<FlowRunsPage>(
    key,
    () =>
      listFlowRuns(pipelineId, flowId, {
        limit: PAGE_SIZE,
      }),
    {
      refreshInterval: (data) =>
        (data?.items ?? []).some((r) => r.state === 'running') ? POLL_MS : 0,
    },
  )

  const runs = useMemo(() => {
    const page = first.data?.items ?? []
    // The poll rewrites page one, and an older page may hold a run that has
    // since moved onto it. De-duplicated by id with page one winning, so a run
    // that settles between two loads is not listed twice in two states.
    const seen = new Set(page.map((r) => r.id))
    return [...page, ...older.filter((r) => !seen.has(r.id))]
  }, [first.data, older])

  const nextCursor = older.length > 0 ? cursor : (first.data?.nextCursor ?? null)

  const loadOlder = useCallback(() => {
    const from = nextCursor
    if (from === null || loadingOlder) return
    setLoadingOlder(true)
    listFlowRuns(pipelineId, flowId, { limit: PAGE_SIZE, cursor: from })
      .then((page) => {
        setOlder((prev) => [...prev, ...page.items])
        setCursor(page.nextCursor)
      })
      .catch(() => {
        // Left to the next attempt: a failed "load older" costs the older page,
        // never the list already on screen, so there is nothing to roll back.
      })
      .finally(() => setLoadingOlder(false))
  }, [nextCursor, loadingOlder, pipelineId, flowId])

  return {
    runs,
    isLoading: first.isLoading,
    error: first.error,
    nextCursor,
    loadingOlder,
    loadOlder,
    live: runs.some((r) => r.state === 'running'),
  }
}
